/**
 * catalogueImportRunner: commit a validated import plan, on any vertical.
 * ───────────────────────────────────────────────────────────────────────────
 * Split out of the modals so the sequencing can be tested without mounting
 * React, and so every API call arrives through an injected function rather
 * than a hard import. That is what lets one runner serve the admin page and a
 * vendor's own dashboard with different auth wiring, and now what lets it
 * serve brand and healthcare with different endpoints.
 *
 * Two phases, in this order and no other: sections, then products. A product's
 * `sectionId` is required and the section must already exist and belong to the
 * vendor, so there is nothing to parallelise across the boundary.
 *
 * PARTIAL IMPORTS ARE THE NORMAL FAILURE MODE, NOT AN ERROR. Each product is
 * its own server-side transaction; there is no transaction spanning the run.
 * So a row that fails is logged, counted, and the run continues. The runner
 * never throws for a row-level problem. Throwing would abandon 60 good rows
 * to save one bad one, and re-running is safe because the validator marks
 * anything already on the vendor as a duplicate and skips it.
 *
 * WHAT A VERTICAL SUPPLIES, AND WHAT IT DOES NOT. The only genuinely
 * vertical-specific thing in a commit is THE BODY: which fields that catalogue
 * stores and what it calls them. Everything else (phase order, the section-id
 * recovery re-read, the upload-once-per-file cache, the extra-angle rule, the
 * log, the progress ticks) is the same work on any catalogue, so it lives here
 * once. A vertical passes `buildBody` and `vendorMedia`, and nothing else.
 *
 * INACTIVE BY DEFAULT IS ENFORCED HERE, for every vertical at once (founder
 * policy, 16 Sep 2026). `forceDisabled` defaults to true and the body builder
 * is handed the resolved `enabled` value rather than deciding it, so there is
 * exactly one place a bulk-loaded catalogue could be made to arrive live, and
 * it is a switch the operator has to turn off on the review screen.
 *
 * NEVER SENT, on any vertical: commissionRate, commissionAmount,
 * vendorNetPrice, commissionCalculatedAt, commissionRateSource, country. All
 * six are server-derived.
 */

/** Tolerates {data:{data:doc}}, {data:doc} and a bare doc. */
const unwrap = (res) => {
  if (!res) return null;
  if (res.data && res.data.data) return res.data.data;
  if (res.data) return res.data;
  return res;
};

const errText = (err, fallback = "failed") =>
  err?.response?.data?.message || err?.message || fallback;

/**
 * @param {object}   plan                 output of the vertical's buildImportPlan
 * @param {object}   deps
 * @param {object}   deps.vendor          { _id, name }
 * @param {Function} deps.createSection   (vendorId, body) => res
 * @param {Function} deps.fetchSections   (vendorId) => res
 * @param {Function} deps.createProduct   (vendorId, sectionId, body) => res
 * @param {Function} deps.uploadImage     (file, opts) => { secure_url }
 * @param {Function} deps.buildBody       ({ item, image, additionalImages, enabled }) => body
 * @param {object}   deps.vendorMedia     { vendorType, entityType, source } for the media ledger
 * @param {boolean}  [deps.forceDisabled] import everything as enabled:false
 * @param {Function} [deps.onProgress]    ({ phase, done, total, label })
 * @returns {Promise<{created, failed, log}>}
 */
export const runCatalogueImport = async (plan, deps) => {
  const {
    vendor,
    createSection,
    fetchSections,
    createProduct,
    uploadImage,
    buildBody,
    vendorMedia = {},
    forceDisabled = true,
    onProgress = () => {},
  } = deps;

  const vendorId = vendor?._id;
  const created = { sections: 0, products: 0, images: 0 };
  const failed = { sections: 0, products: 0 };
  const log = [];
  const say = (level, message) => log.push({ level, message });

  const okItems = plan.items.filter((i) => i.ok);
  const sectionsToCreate = plan.sections.filter((s) => s.action === "create");
  const totalSteps = sectionsToCreate.length + okItems.length;
  let step = 0;
  const tick = (phase, label) => {
    step += 1;
    onProgress({ phase, done: step, total: totalSteps, label });
  };

  // Rows the plan already decided not to send. Reported here so the run log is
  // a complete account of the workbook, not just of what was attempted.
  for (const item of plan.items) {
    if (item.skipped) say("warn", `${item.name || `row ${item.sourceRow}`}: skipped — ${item.skipReason}`);
  }

  // ── Phase 1: sections ────────────────────────────────────────────────────
  const sectionIdByTitle = new Map();
  for (const section of plan.sections) {
    if (section.action === "existing" && section.existingId) {
      sectionIdByTitle.set(section.title.toLowerCase(), section.existingId);
    }
  }

  for (const section of sectionsToCreate) {
    tick("sections", section.title);
    try {
      const res = await createSection(vendorId, {
        title: section.title,
        description: section.description || "",
      });
      const doc = unwrap(res);
      const id = doc?._id;
      if (id) {
        sectionIdByTitle.set(section.title.toLowerCase(), id);
        created.sections += 1;
        say("ok", `Section "${section.title}" created`);
      } else {
        // Created but the id did not come back in a shape we recognise. The
        // recovery re-read below will find it, so this is not fatal yet.
        say("warn", `Section "${section.title}": created but no id came back`);
      }
    } catch (err) {
      failed.sections += 1;
      say("error", `Section "${section.title}" failed: ${errText(err)}`);
    }
  }

  // One re-read covers every section whose id we did not capture — including
  // one that already existed under a title the plan did not match, which would
  // otherwise strand every product in it.
  const missing = plan.sections.filter(
    (s) => !sectionIdByTitle.has(s.title.toLowerCase())
  );
  if (missing.length) {
    try {
      const fresh = unwrap(await fetchSections(vendorId)) || [];
      for (const s of fresh) {
        if (s?.title && s?._id) {
          const key = s.title.toLowerCase();
          if (!sectionIdByTitle.has(key)) sectionIdByTitle.set(key, s._id);
        }
      }
    } catch (err) {
      say("warn", `Could not re-read sections: ${errText(err)}`);
    }
  }

  // ── Phase 2: products ────────────────────────────────────────────────────

  /* One upload per FILE, not per row that mentions it.
     A catalogue imported ahead of its photography points every row at the same
     placeholder image; without this that is 71 identical uploads, 71 Cloudinary
     assets to bill and clean up, and 71 chances for one to fail. Keyed on the
     File object itself, which the parser hands back by identity for a given
     filename, so two rows naming the same photo genuinely share one asset. */
  const uploadedByFile = new Map();

  /** A picked file becomes a Cloudinary URL; a URL passes straight through. */
  const resolveUrl = async (source, purpose, itemName) => {
    if (!source) return null;
    if (source.kind === "url") return source.url;
    if (source.kind !== "file") return null;
    if (uploadedByFile.has(source.file)) return uploadedByFile.get(source.file);
    const res = await uploadImage(source.file, {
      skipValidation: true,
      vendorMedia: {
        ...vendorMedia,
        vendorId,
        assetPurpose: purpose,
        entityId: itemName,
      },
    });
    const url = res?.secure_url;
    if (!url) throw new Error("image upload returned no URL");
    uploadedByFile.set(source.file, url);
    created.images += 1;
    return url;
  };

  for (const item of okItems) {
    tick("products", item.name);
    const sectionId = sectionIdByTitle.get(item.sectionTitle.toLowerCase());
    if (!sectionId) {
      failed.products += 1;
      say("error", `${item.name}: section "${item.sectionTitle}" is not available, skipped`);
      continue;
    }

    try {
      const image = await resolveUrl(item.cover, "product_cover", item.name);
      if (!image) throw new Error("no cover image could be resolved");

      /* Extra angles, resolved through the SAME Cloudinary path as the cover
         but with the `product_angle` preset. An extra angle is never worth
         failing the product over: a product with one good photo beats no
         product at all. */
      const additionalImages = [];
      for (const source of item.additional) {
        try {
          const url = await resolveUrl(source, "product_angle", item.name);
          if (url) additionalImages.push(url);
        } catch (err) {
          say("warn", `${item.name}: extra angle skipped — ${errText(err)}`);
        }
      }

      const body = buildBody({
        item,
        image,
        additionalImages,
        /* Imports land deactivated by default. A bulk-loaded catalogue has not
           been through anyone's eyes yet, and the vendor's reputation is
           carried by whatever a shopper sees first. Resolved here rather than
           in each body builder so no vertical can forget the rule. */
        enabled: forceDisabled ? false : item.enabled,
        sectionId,
        vendorId,
      });

      await createProduct(vendorId, sectionId, body);
      created.products += 1;
      say("ok", `${item.name} created`);
    } catch (err) {
      failed.products += 1;
      say("error", `${item.name} failed: ${errText(err, "unknown error")}`);
    }
  }

  return { created, failed, log };
};

export default runCatalogueImport;

/* Exported for the tests only — the helpers above are deliberately private. */
export const __internals = { unwrap, errText };
